/**
 * The URL search of `/admin/users` (`q`, `page`, `role`, `sort`, `dir`): how a
 * link becomes list parameters, and how clicking a column header changes it.
 *
 * Aliases: users url state, users search params, admin users sort toggle
 */

import { z } from "zod";

import type { AdminUserListParams } from "~/lib/functions/admin-users";
import { ADMIN_USER_SORTS, type AdminUserSort } from "~/lib/shared/admin-users";
import { roleKeySchema } from "~/lib/shared/roles";
import {
  nextSortSearch as nextSort,
  SORT_DIRECTIONS,
  type SortDefaults,
  type SortDirection,
} from "~/lib/shared/sort";

export const PAGE_SIZE = 25;

/** Names read best A–Z; Joined starts newest first. */
const USER_SORT_DEFAULTS: SortDefaults<AdminUserSort> = {
  sort: "joined",
  direction: (sort) => (sort === "name" ? "asc" : "desc"),
};

// A stale or hand-edited link drops the params it gets wrong instead of
// failing the whole page: `?role=nonsense` shows the unfiltered list.
export const usersSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  role: roleKeySchema.optional().catch(undefined),
  sort: z.enum(ADMIN_USER_SORTS).optional().catch(undefined),
  dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
});

export type UsersSearch = z.infer<typeof usersSearchSchema>;

export function listParamsFrom(search: UsersSearch): AdminUserListParams {
  const sort = search.sort ?? USER_SORT_DEFAULTS.sort;
  return {
    search: search.q ?? "",
    page: search.page ?? 1,
    pageSize: PAGE_SIZE,
    role: search.role,
    sort,
    direction: search.dir ?? USER_SORT_DEFAULTS.direction(sort),
  };
}

/** The search change for a click on `column`'s header; the list returns to page 1. */
export function nextSortSearch(
  current: { sort: AdminUserSort; direction: SortDirection },
  column: AdminUserSort,
): Pick<UsersSearch, "sort" | "dir" | "page"> {
  return { ...nextSort(current, column, USER_SORT_DEFAULTS), page: undefined };
}
